import { z } from 'zod'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'
import {
  type LearningQuestionMatchingEntity,
  type LearningQuestionMultipleChoiceEntity,
  type LearningQuestionEntity,
  LearningQuestionSchema,
  type LearningQuestionSequencingEntity,
  type LearningQuestionSingleChoiceEntity,
  type LearningQuestionTrueFalseEntity
} from '@/core/infrastructure/schemas/quiz/QuizQuestionSchema.ts'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'
import {
  type QuizQuestionAnswerEntity,
  type QuizQuestionAnswerMatchingEntity,
  QuizQuestionAnswerMatchingSchema,
  type QuizQuestionAnswerMultipleChoiceEntity,
  type QuizQuestionAnswerSequencingEntity,
  QuizQuestionAnswerSequencingSchema,
  type QuizQuestionAnswerSingleChoiceEntity,
  type QuizQuestionAnswerTrueFalseEntity,
  QuizQuestionAnswerTrueFalseSchema
} from '@/core/infrastructure/schemas/quiz/QuizQuestionAnswerSchema.ts'
import {
  QuizVersionListSchema,
  QuizVersionSchema
} from '@/core/infrastructure/schemas/quiz/QuizVersionSchemas.ts'

// Main Learning Quiz schema
export const QuizSchema = z.object({
  id: z.number(),
  type: z.nativeEnum(QuizType.Values),
  versions: QuizVersionListSchema
})

// Multiple quizzes schema
export const QuizListSchema = z.array(QuizSchema)

// Learning Quiz entity
export type QuizEntity = z.infer<typeof QuizSchema>
export type QuizListEntity = z.infer<typeof QuizListSchema>

// Type guards for different question types
export const isSingleChoiceQuestion = (
  question: LearningQuestionEntity
): question is LearningQuestionSingleChoiceEntity => {
  return question.type === LearningQuizQuestionType.Values.SINGLE_CHOICE
}

export const isMultipleChoiceQuestion = (
  question: LearningQuestionEntity
): question is LearningQuestionMultipleChoiceEntity => {
  return question.type === LearningQuizQuestionType.Values.MULTIPLE_CHOICE
}

export const isTrueFalseQuestion = (
  question: LearningQuestionEntity
): question is LearningQuestionTrueFalseEntity => {
  return question.type === LearningQuizQuestionType.Values.TRUE_FALSE
}

export const isMatchingQuestion = (
  question: LearningQuestionEntity
): question is LearningQuestionMatchingEntity => {
  return question.type === LearningQuizQuestionType.Values.MATCHING
}

export const isSequencingQuestion = (
  question: LearningQuestionEntity
): question is LearningQuestionSequencingEntity => {
  return question.type === LearningQuizQuestionType.Values.SEQUENCING
}

// Type guards for different answer types
export const isSingleChoiceAnswer = (
  answer: QuizQuestionAnswerEntity
): answer is QuizQuestionAnswerSingleChoiceEntity => {
  return answer.type === LearningQuizQuestionType.Values.SINGLE_CHOICE
}

export const isMultipleChoiceAnswer = (
  answer: QuizQuestionAnswerEntity
): answer is QuizQuestionAnswerMultipleChoiceEntity => {
  return answer.type === LearningQuizQuestionType.Values.MULTIPLE_CHOICE
}

export const isTrueFalseAnswer = (
  answer: QuizQuestionAnswerEntity
): answer is QuizQuestionAnswerTrueFalseEntity => {
  return answer.type === LearningQuizQuestionType.Values.TRUE_FALSE
}

export const isMatchingAnswer = (
  answer: QuizQuestionAnswerEntity
): answer is QuizQuestionAnswerMatchingEntity => {
  return answer.type === LearningQuizQuestionType.Values.MATCHING
}

export const isSequencingAnswer = (
  answer: QuizQuestionAnswerEntity
): answer is QuizQuestionAnswerSequencingEntity => {
  return answer.type === LearningQuizQuestionType.Values.SEQUENCING
}
